import React, { useCallback, useMemo } from 'react'
import { FormTextField, FormToggle, useZionFormContext } from '@fs/zion-form'
import { Cell, FlowGrid, LabelSpacer, Paragraph, Row } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import {
  useSlsHighlightsContext,
  useSlsStateContext,
  getDisplayLabelForFieldType,
  getNestedOrFloatingElementById,
} from '@fs/zion-sls'
import FormStandardizedSexField from '../../../standardizedSexField/StandardizedSexField'
import { getHighlightIdsByFieldId } from '../../shared/utils/highlightUtils'
import HighlightButton from '../../panels/shared/HighlightButton'
import { useSexFieldValidation } from '../hooks/useSexFieldValidation'

/**
 * PersonalDetailsForm component renders a form section for entering personal details such as sex, race, and age.
 * It integrates with form, sls, and highlight contexts to manage form state and highlight interactions.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.person - The person object containing personal details to pre-populate the form
 * @param {boolean} props.forcePrimaryPerson - Whether the toggle should be disabled (true for CENSUS records)
 * @returns {JSX.Element} The rendered form section for personal details
 */
const PersonalDetailsForm = ({ person, forcePrimaryPerson, splitScreenImageArk }) => {
  const [t] = useTranslation()
  const {
    formState: { defaultValues },
    getValues,
  } = useZionFormContext()
  const h = useSlsHighlightsContext()
  const { types: slsTypes, images } = useSlsStateContext()

  // Extract default field values for sex to determine if the field was pre-populated
  const defaultSexField = useMemo(() => defaultValues?.SEX_CODE?.value, [defaultValues])

  // Handlers to manage highlight hover on input focus/blur
  const handleInputFocus = useCallback(
    (fieldName) => {
      const fieldId = getValues(fieldName)
      const { mainHighlights } = h.getHighlights()
      const fieldHighlightsIds = getHighlightIdsByFieldId(mainHighlights, fieldId)

      h.hoverHighlights({ elementIds: fieldHighlightsIds })
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getValues]
  )

  const handleInputBlur = useCallback(() => {
    h.hoverHighlights({ elementIds: [] })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Prepare field object for HighlightButton component
  const prepareFieldForHighlights = useCallback(
    (type) => {
      const currentField = getValues(type)

      return {
        ...currentField,
        displayLabel: getDisplayLabelForFieldType(type, slsTypes),
        name: `${type}.value`,
        fieldType: type,
        elementType: 'FIELD',
        ...getNestedOrFloatingElementById(images, currentField.id),
      }
    },
    [getValues, images, slsTypes]
  )

  const validationProps = useSexFieldValidation({ fieldName: 'SEX_CODE.value' })

  return (
    <FlowGrid orientation="vertical" columnCount={1} gutters="nano">
      <Paragraph size="sm">
        <strong>{t('essentialInformationForm.personalDetails.title', 'Personal Details')}</strong>
      </Paragraph>

      <FormToggle
        name="primaryPerson"
        label={t('essentialInformationForm.primaryPerson', 'Is primary person for this record')}
        data-testid="primary-person-toggle"
        disabled={forcePrimaryPerson}
        title={
          forcePrimaryPerson
            ? t('essentialInformationForm.primaryPerson.disabledTitle', 'All persons on a census are primary')
            : ''
        }
      />

      <Row gutters="xxs">
        {/* Adding this check to prevent errors in case person data is not available, as the standardized sex field
        relies on person data to function properly. This input will throw an error if the user changes images with the form in edit mode
        because the person will become undefined */}
        {person && (
          <FormStandardizedSexField
            fieldName="SEX_CODE.value"
            addedSexCode={!defaultSexField ? { id: 'SEX_CODE' } : null}
            person={person}
            autosuggestProps={{
              dense: true,
              label: getDisplayLabelForFieldType('SEX_CODE', slsTypes),
              onFocus: () => handleInputFocus('SEX_CODE.id'),
              onBlur: handleInputBlur,
              'data-testid': 'sex-code-field',
              clearOnClickAway: true, // Clear the field if user clicks away without selecting a value to prevent invalid values from being saved
              ignoreTouchedForWarnings: true,
              ...validationProps, // Spread validation props to show warning or success messages based on the selected value
            }}
            growX
          />
        )}
        <Cell>
          <LabelSpacer />
          <HighlightButton
            field={prepareFieldForHighlights('SEX_CODE')}
            splitScreenImageArk={splitScreenImageArk}
            buttonProps={{
              'data-testid': 'sex-code-highlight-button',
            }}
            linkName="essential-information-sex-code-highlight"
            growX
            iconOnly
          />
        </Cell>
      </Row>

      <Row gutters="xxs">
        <FormTextField
          name="RACE_OR_COLOR.value"
          label={getDisplayLabelForFieldType('RACE_OR_COLOR', slsTypes)}
          onFocus={() => handleInputFocus('RACE_OR_COLOR.id')}
          onBlur={handleInputBlur}
          data-testid="race-or-color-field"
          dense
          growX
        />
        <Cell>
          <LabelSpacer />
          <HighlightButton
            field={prepareFieldForHighlights('RACE_OR_COLOR')}
            splitScreenImageArk={splitScreenImageArk}
            buttonProps={{
              'data-testid': 'race-or-color-highlight-button',
            }}
            growX
            iconOnly
          />
        </Cell>
      </Row>

      <Row gutters="xxs">
        <FormTextField
          name="AGE.value"
          label={getDisplayLabelForFieldType('AGE', slsTypes)}
          onFocus={() => handleInputFocus('AGE.id')}
          onBlur={handleInputBlur}
          data-testid="age-field"
          dense
          growX
        />
        <Cell>
          <LabelSpacer />
          <HighlightButton
            field={prepareFieldForHighlights('AGE')}
            splitScreenImageArk={splitScreenImageArk}
            buttonProps={{
              'data-testid': 'age-highlight-button',
            }}
            growX
            iconOnly
          />
        </Cell>
      </Row>
    </FlowGrid>
  )
}

export default PersonalDetailsForm
